import {
	FilesetResolver,
	HandLandmarker,
	Landmark,
	NormalizedLandmark,
	PoseLandmarker,
	PoseLandmarkerResult,
} from '@mediapipe/tasks-vision';
import { landmarksToRemove } from '@pages/PostureScan/constants';
import {
	boneColor,
	circleRadius,
	drawHandJoint,
	drawHip,
	drawShoulders,
	drawSpineLikeReference,
	drawStylizedBone,
	handBonePairs,
} from '@utils/SkeletonMedia';
import { memo, useCallback, useEffect, useRef } from 'react';

const defaultVideoSize = { width: 1280, height: 720 };

const drawOptions = { color: 'white', lineWidth: 5, visibilityMin: 0.65 };

function throttle<T extends (...args: any[]) => void>(
	func: T,
	delay: number,
): (...args: Parameters<T>) => void {
	let lastCall = 0;
	return (...args: Parameters<T>): void => {
		const now = performance.now();
		if (now - lastCall < delay) return;
		lastCall = now;
		func(...args);
	};
}

export const highlightedBoneColor = '#6847C6';

function Mediapipe(props: {
	cameraId: string | null;
	isFullscreen: boolean;
	isSwitchMode: boolean;
	isRightHand?: boolean;
	wristExercise?: boolean;
	stopLandmarks?: boolean;
	bodyPointsToHighlight?: number[];
	videoSizeState?: string;
	setCanvasDimensions: (dimensions: { width: number; height: number }) => void;
}) {
	const {
		cameraId,
		wristExercise,
		stopLandmarks,
		isFullscreen,
		bodyPointsToHighlight = [],
		isRightHand,
		videoSizeState,
		setCanvasDimensions,
	} = props;
	const videoRef = useRef<HTMLVideoElement>(null);
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const canvasHandRef = useRef<HTMLCanvasElement>(null);
	const poseLandmarkerRef = useRef<PoseLandmarker | null>(null);
	const handLandmarkerRef = useRef<HandLandmarker | null>(null);
	const lastFrameTimeRef = useRef<number>(0);

	const isIpad =
		/iPad|Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1;

	const videoSize = defaultVideoSize;
	const frameRate = isIpad ? { ideal: 10, max: 15 } : { ideal: 15, max: 20 };

	const handColors = {
		Left: isRightHand ? boneColor : highlightedBoneColor,
		Right: isRightHand ? highlightedBoneColor : boneColor,
		Unknown: boneColor,
	};

	let animationFrameId: number;

	const triggerResultsEvent = (eventName: string, data: unknown) => {
		const event = new CustomEvent(eventName, { detail: data });
		window.dispatchEvent(event);
	};
	const throttledTriggerResultsEvent = throttle(triggerResultsEvent, 500);
	const throttledTriggerHandResults = throttle(triggerResultsEvent, 500);

	const drawCallback = useCallback(
		(results: PoseLandmarkerResult) => {
			if (!results.landmarks[0] || !canvasRef.current) return;

			lastFrameTimeRef.current = performance.now();

			const canvasCtx = canvasRef.current.getContext(
				'2d',
			) as CanvasRenderingContext2D;

			throttledTriggerResultsEvent('results', {
				results: results.landmarks[0],
			});

			const canvas = canvasRef.current;
			const ctx = canvas.getContext('2d');
			const video = videoRef.current;
			if (!ctx || !video) return;

			const width = (canvas.width = video.videoWidth);
			const height = (canvas.height = video.videoHeight);

			ctx.save();
			ctx.clearRect(0, 0, width, height);

			const landmarks = results.landmarks[0];

			const transform = (
				landmark: Landmark,
			): { x: number; y: number; z: number } => ({
				x: landmark.x * canvas.width,
				y: landmark.y * canvas.height,
				z: landmark.z ? landmark.z * canvas.width : 0,
			});

			const bonePairs = [
				{ points: [11, 13] },
				{ points: [13, 15] },
				{ points: [12, 14] },
				{ points: [14, 16] },
				{ points: [23, 25] },
				{ points: [25, 27] },
				{ points: [24, 26] },
				{ points: [26, 28] },
			];

			bonePairs.forEach(({ points: [startIdx, endIdx] }) => {
				const start = landmarks[startIdx];
				const end = landmarks[endIdx];

				if (
					start.visibility < drawOptions.visibilityMin ||
					end.visibility < drawOptions.visibilityMin
				)
					return;

				const p1 = transform(start);
				const p2 = transform(end);

				const shouldHighlight =
					bodyPointsToHighlight.includes(startIdx) ||
					bodyPointsToHighlight.includes(endIdx);

				const colorToUse = shouldHighlight ? highlightedBoneColor : boneColor;

				drawStylizedBone(
					ctx as CanvasRenderingContext2D,
					p1.x,
					p1.y,
					p2.x,
					p2.y,
					20,
					2,
					2,
					colorToUse,
				);
			});

			const shouldHighlight =
				bodyPointsToHighlight.includes(11) ||
				bodyPointsToHighlight.includes(12);
			const colorToUseShoulder = shouldHighlight
				? highlightedBoneColor
				: boneColor;
			drawShoulders(ctx, landmarks, transform, colorToUseShoulder);
			const shouldHighlightHip =
				bodyPointsToHighlight.includes(23) ||
				bodyPointsToHighlight.includes(24);
			const colorToUseHip = shouldHighlightHip
				? highlightedBoneColor
				: boneColor;
			drawHip(ctx, landmarks, transform, colorToUseHip);
			const spineLikeReferenceColor = bodyPointsToHighlight.includes(33)
				? highlightedBoneColor
				: boneColor;
			drawSpineLikeReference(ctx, landmarks, spineLikeReferenceColor);

			landmarks.forEach((lm, idx) => {
				if (
					landmarksToRemove.includes(idx) ||
					lm.visibility < drawOptions.visibilityMin
				)
					return;
				const { x, y } = transform(lm);
				ctx.beginPath();
				const jointColor = bodyPointsToHighlight.includes(idx)
					? highlightedBoneColor
					: boneColor;
				ctx.arc(x, y, circleRadius, 0, 2 * Math.PI);
				ctx.fillStyle = jointColor;
				ctx.fill();
			});
			canvasCtx.restore();
		},
		[throttledTriggerResultsEvent, videoSize],
	);

	const drawHandCallback = async () => {
		if (
			videoRef.current &&
			handLandmarkerRef.current &&
			canvasHandRef.current
		) {
			const handResults = await handLandmarkerRef.current.detectForVideo(
				videoRef.current,
				performance.now(),
			);

			if (
				handResults &&
				handResults.landmarks &&
				handResults.landmarks.length > 0 &&
				canvasHandRef.current
			) {
				lastFrameTimeRef.current = performance.now();
				const canvasCtx = canvasHandRef.current.getContext(
					'2d',
				) as CanvasRenderingContext2D;

				const canvas = canvasHandRef.current;
				const video = videoRef.current;
				if (!video) return;

				const width = (canvas.width = video.videoWidth);
				const height = (canvas.height = video.videoHeight);

				setCanvasDimensions({
					width: video.videoWidth,
					height: video.videoHeight,
				});

				let leftHand: NormalizedLandmark[] | null = null;
				let rightHand: NormalizedLandmark[] | null = null;
				canvasCtx.clearRect(0, 0, width, height);

				handResults.landmarks.forEach((handLandmarks, index) => {
					if (handLandmarks.length === 21) {
						const handedness =
							handResults.handednesses[index] &&
							handResults.handednesses[index][0] &&
							handResults.handednesses[index][0].categoryName;

						if (handedness === 'Left') {
							leftHand = handLandmarks;
						} else if (handedness === 'Right') {
							rightHand = handLandmarks;
						}

						const handColor =
							handColors[handedness as 'Left' | 'Right' | 'Unknown'] ||
							handColors.Unknown;

						handBonePairs.forEach(
							({ points: [startIdx, endIdx], thickness }) => {
								const start = handLandmarks[startIdx];
								const end = handLandmarks[endIdx];

								const p1 = {
									x: start.x * width,
									y: start.y * height,
								};

								const p2 = {
									x: end.x * width,
									y: end.y * height,
								};

								const boneLength = Math.sqrt(
									Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2),
								);

								drawStylizedBone(
									canvasCtx,
									p1.x,
									p1.y,
									p2.x,
									p2.y,
									boneLength * 0.15,
									thickness,
									thickness * 0.8,
									handColor,
								);
							},
						);

						handLandmarks.forEach((landmark: NormalizedLandmark, idx) => {
							const x = landmark.x * width;
							const y = landmark.y * height;

							// Different sizes for different landmarks
							let jointRadius = circleRadius * 0.8;

							// Make wrist larger
							if (idx === 0) jointRadius = circleRadius * 1.2;
							// Make fingertips slightly larger and more prominent
							else if ([4, 8, 12, 16, 20].includes(idx))
								jointRadius = circleRadius;
							// Make middle joints smaller
							else jointRadius = circleRadius * 0.7;

							drawHandJoint(canvasCtx, x, y, jointRadius, handColor);
						});
					}

					throttledTriggerHandResults('handmarks', {
						handmarkLeft: leftHand,
						handmarkRight: rightHand,
					});
				});
			}
		}
	};

	const predictWebcam = useCallback(async () => {
		if (videoRef.current && poseLandmarkerRef.current) {
			const startTimeMs = performance.now();
			if (videoRef.current.readyState < 2) {
				console.error('Video not ready for processing.');
				return;
			}
			try {
				await poseLandmarkerRef.current.detectForVideo(
					videoRef.current,
					startTimeMs,
					drawCallback,
				);
			} catch (error) {
				console.error('PoseLandmarker Error:', error);
				return;
			}
			if (wristExercise && handLandmarkerRef.current) {
				try {
					await drawHandCallback();
				} catch (error) {
					console.error('HandLandmarker Error:', error);
				}
			}
			animationFrameId = requestAnimationFrame(predictWebcam);
		}
	}, [drawCallback, wristExercise]);

	const stopStreamedVideo = useCallback(() => {
		if (videoRef.current) {
			cancelAnimationFrame(animationFrameId);
			const stream = videoRef.current.srcObject as MediaStream;
			if (stream) {
				stream.getTracks().forEach(track => track.stop());
			}
			videoRef.current.srcObject = null;
			videoRef.current.removeEventListener('loadeddata', predictWebcam);
		}
	}, [predictWebcam]);

	const setupCamera = useCallback(() => {
		if (!navigator.mediaDevices?.getUserMedia) {
			console.warn('getUserMedia() is not supported by your browser');
			return;
		}
		if (!poseLandmarkerRef.current) {
			console.warn('Wait! PoseLandmarker not loaded yet.');
			setTimeout(setupCamera, 1000);
			return;
		}
		const constraints = {
			video: {
				deviceId: { exact: cameraId as string },
				width: { ideal: videoSize.width, max: videoSize.width },
				height: { ideal: videoSize.height, max: videoSize.height },
				frameRate: frameRate,
			},
			audio: false,
		};
		navigator.mediaDevices.getUserMedia(constraints).then(stream => {
			if (videoRef.current) {
				videoRef.current.srcObject = stream;
				videoRef.current.addEventListener('loadeddata', () => {
					predictWebcam();
					// setupFreezeDetection();
				});
			}
		});
	}, [cameraId, predictWebcam, videoSize, frameRate]);

	const setupCameraOnly = useCallback(() => {
		if (!navigator.mediaDevices?.getUserMedia) {
			console.warn('getUserMedia() is not supported by your browser');
			return;
		}

		const constraints = {
			video: {
				deviceId: { exact: cameraId as string },
				width: { ideal: videoSize.width, max: videoSize.width },
				height: { ideal: videoSize.height, max: videoSize.height },
				frameRate: frameRate,
			},
			audio: false,
		};
		navigator.mediaDevices.getUserMedia(constraints).then(stream => {
			if (videoRef.current) {
				videoRef.current.srcObject = stream;
			}
		});
	}, [cameraId, videoSize, frameRate]);

	const createPoseLandmarker = useCallback(async () => {
		const vision = await FilesetResolver.forVisionTasks(
			'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm',
		);
		const newPoseLandmarker = await PoseLandmarker.createFromOptions(vision, {
			baseOptions: {
				modelAssetPath:
					'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_full/float16/latest/pose_landmarker_full.task',
				delegate: isIpad ? 'GPU' : 'CPU',
			},
			runningMode: 'VIDEO',
			numPoses: 1,
			minPoseDetectionConfidence: 0.6,
			minTrackingConfidence: 0.6,
		});
		poseLandmarkerRef.current = newPoseLandmarker;
	}, [isIpad]);

	const createHandLandmarker = useCallback(async () => {
		const vision = await FilesetResolver.forVisionTasks(
			'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm',
		);
		const newHandLandmarker = await HandLandmarker.createFromOptions(vision, {
			baseOptions: {
				modelAssetPath:
					'https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/1/hand_landmarker.task',
				delegate: isIpad ? 'GPU' : 'CPU', // Use GPU on iPad for better performance
			},
			runningMode: 'VIDEO',
			numHands: 2,
			minHandDetectionConfidence: 0.5,
			minHandPresenceConfidence: 0.5,
			minTrackingConfidence: 0.5,
		});
		handLandmarkerRef.current = newHandLandmarker;
	}, [isIpad]);

	useEffect(() => {
		if (stopLandmarks) {
			return;
		}
		createPoseLandmarker();
		if (wristExercise) {
			createHandLandmarker();
		}
		return () => {
			stopStreamedVideo();
			if (poseLandmarkerRef.current) {
				console.log('Releasing PoseLandmarker instance...');
				poseLandmarkerRef.current.close();
				poseLandmarkerRef.current = null;
			}
			if (handLandmarkerRef.current) {
				console.log('Releasing HandLandmarker instance...');
				handLandmarkerRef.current.close();
				handLandmarkerRef.current = null;
			}
		};
	}, [
		createPoseLandmarker,
		createHandLandmarker,
		stopStreamedVideo,
		wristExercise,
		stopLandmarks,
	]);

	useEffect(() => {
		if (stopLandmarks) {
			if (poseLandmarkerRef.current) {
				poseLandmarkerRef.current.close();
				poseLandmarkerRef.current = null;
			}
			if (handLandmarkerRef.current) {
				handLandmarkerRef.current.close();
				handLandmarkerRef.current = null;
			}
			return;
		}
	}, [stopLandmarks]);

	useEffect(() => {
		if (cameraId) {
			if (stopLandmarks) {
				setupCameraOnly();
			} else {
				setupCamera();
			}
		}
	}, [cameraId, setupCamera, setupCameraOnly, stopLandmarks]);

	return (
		<div id="printscreen">
			<video
				id="video"
				autoPlay
				playsInline
				ref={videoRef}
				onLoadedMetadata={() => {
					const video = videoRef.current;
					const canvas = canvasRef.current;
					const handCanvas = canvasHandRef.current;
					if (video && canvas) {
						canvas.width = video.videoWidth;
						canvas.height = video.videoHeight;
						setCanvasDimensions({
							width: video.videoWidth,
							height: video.videoHeight,
						});
					}
					if (video && handCanvas) {
						handCanvas.width = video.videoWidth;
						handCanvas.height = video.videoHeight;
					}
				}}
				style={{
					position: 'absolute',
					top: 0,
					left: 0,
					width: '100%',
					height: isFullscreen ? '97vh' : '100%',
					objectFit: videoSizeState as React.CSSProperties['objectFit'],
					zIndex: 1,
					pointerEvents: 'none',
					background: 'var(--color-background-video-element)',
				}}
			/>
			<canvas
				id="canvas"
				ref={canvasRef}
				style={{
					position: 'absolute',
					top: 0,
					left: 0,
					width: '100%',
					height: isFullscreen ? '97vh' : '100%',
					objectFit: videoSizeState as React.CSSProperties['objectFit'],
					zIndex: 2,
					pointerEvents: 'none',
				}}
			/>
			<canvas
				ref={canvasHandRef}
				style={{
					position: 'absolute',
					top: 0,
					left: 0,
					width: '100%',
					height: isFullscreen ? '97vh' : '100%',
					objectFit: videoSizeState as React.CSSProperties['objectFit'],
					zIndex: 3,
					pointerEvents: 'none',
				}}
			/>
		</div>
	);
}

export default memo(Mediapipe);
